/**
 * Firebase のメールアクション（/auth/action）まわりの純関数。
 *
 * Firebase ホスト型の `/__/auth/action` は Google 配信の英語ページで、
 * `languageCode` でも Console でも日本語にできないため自前ページへ置き換える。
 * アクション URL は Firebase プロジェクト単位の設定なので、
 * verifyEmail だけでなく resetPassword / recoverEmail / signIn も同じパスへ届く。
 */

export const AUTH_ACTION_PATH = '/auth/action'

/** Firebase が `mode` に載せてくる値のうち、当サービスで発生しうるもの。 */
const AUTH_ACTION_MODES = [
  'verifyEmail',
  'verifyAndChangeEmail',
  'resetPassword',
  'recoverEmail',
  'signIn'
] as const

export type AuthActionMode = (typeof AUTH_ACTION_MODES)[number]

/**
 * verifyEmail メールに付ける continueUrl。
 *
 * 「履歴書を作る → メールで送るためにメール認証 → 作成画面に戻って送信」という導線なので、
 * 確認後に元の作成画面へ戻れるよう、送信元の画面をそのまま載せる。
 * クエリ・ハッシュはメール本文に載せたくないので落とす。
 *
 * 同じビルドが複数ホスト（rirekisho / アプリ用フロント / keirekisho）で配信されるため、
 * ビルド時の VUE_APP_SITE_URL ではなく実際に開いているオリジンを使う。
 */
export function verifyEmailActionCodeSettings(): { url: string } {
  return { url: `${window.location.origin}${window.location.pathname}` }
}

/**
 * resetPassword メールに付ける continueUrl。
 *
 * 再設定後の行き先はアクションページが決める（ログイン画面）ので、
 * ここでは「どのホストから送られたか」だけを載せる。
 */
export function passwordResetActionCodeSettings(): { url: string } {
  return { url: window.location.origin }
}

/** パスワードの最小文字数（Firebase の既定と同じ）。 */
export const PASSWORD_MIN_LENGTH = 6

export function queryValue(value: unknown): string {
  if (Array.isArray(value)) return String(value[0] ?? '')
  return value === undefined || value === null ? '' : String(value)
}

export function isAuthActionMode(mode: string): mode is AuthActionMode {
  return (AUTH_ACTION_MODES as readonly string[]).includes(mode)
}

/**
 * continueUrl として許可するオリジン一覧を env 文字列から取り出す。
 * 同一オリジンは常に許可されるので、ここに書くのは兄弟サイトだけでよい。
 */
export function parseAllowedOrigins(raw: string | undefined): string[] {
  if (!raw) return []
  return raw
    .split(',')
    .map((value) => value.trim().replace(/\/$/, ''))
    .filter(Boolean)
}

/**
 * `continueUrl` は Firebase から渡ってくる外部入力なので、オープンリダイレクトに
 * ならないよう「同一オリジン」か「明示的に許可したオリジン」だけ通す。
 * 判定できないものは呼び出し側の既定へフォールバックさせるため null を返す。
 *
 * Firebase は authorizedDomains で同種の検査をしている。同じ Firebase プロジェクトを
 * 共有する別ホスト（アプリ用フロント等）からのメールリンクを元のホストへ戻すために、
 * 許可オリジンは env（`VUE_APP_AUTH_ACTION_ALLOWED_ORIGINS`）で与える。
 */
export function safeContinueUrl(
  rawUrl: unknown,
  origin: string,
  allowedOrigins: string[] = []
): string | null {
  const url = queryValue(rawUrl)
  if (!url) return null
  try {
    const parsed = new URL(url, origin)
    if (parsed.origin === origin) return parsed.toString()
    return allowedOrigins.includes(parsed.origin) ? parsed.toString() : null
  } catch {
    return null
  }
}

/**
 * メールリンクログイン（mode=signIn）を従来どおり continueUrl 側で受けるための転送先。
 * ホスト型ページと同じく、continueUrl に元のクエリをそのまま引き継ぐ。
 */
export function emailLinkSignInForwardUrl(
  query: Record<string, unknown>,
  origin: string,
  fallbackUrl: string,
  allowedOrigins: string[] = []
): string {
  const candidate = safeContinueUrl(query.continueUrl, origin, allowedOrigins)
  const base =
    candidate && new URL(candidate, origin).pathname !== AUTH_ACTION_PATH
      ? candidate
      : fallbackUrl
  const forward = new URL(base, origin)
  Object.keys(query).forEach((key) => {
    if (key === 'continueUrl') return
    forward.searchParams.set(key, queryValue(query[key]))
  })
  return forward.toString()
}

function authErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined
  const code = (error as { code?: unknown }).code
  return typeof code === 'string' ? code : undefined
}

/**
 * oobCode が使えなくなる（＝画面をやり直すしかない）エラーかどうか。
 * これ以外は入力し直せば復帰できるので、フォームを畳まない。
 */
const TERMINAL_ERROR_CODES = new Set([
  'auth/expired-action-code',
  'auth/invalid-action-code',
  'auth/user-disabled',
  'auth/user-not-found'
])

export function isTerminalActionCodeError(error: unknown): boolean {
  const code = authErrorCode(error)
  return !!code && TERMINAL_ERROR_CODES.has(code)
}

/**
 * oobCode 系のエラーは「期限切れ / 使用済み / 壊れている」がほぼ全てで、
 * 利用者にとっての対処は同じ（メールを送り直す）なので文言を寄せる。
 */
export function messageForAuthActionError(error: unknown): string {
  switch (authErrorCode(error)) {
    case 'auth/expired-action-code':
      return 'リンクの有効期限が切れています。お手数ですが、もう一度メールを送信してください。'
    case 'auth/invalid-action-code':
      return 'このリンクは既に使用済みか、正しくありません。お手数ですが、もう一度メールを送信してください。'
    case 'auth/user-disabled':
      return 'サービスの利用が停止されています。'
    case 'auth/user-not-found':
      return 'アカウントが見つかりませんでした。'
    case 'auth/weak-password':
      return `パスワードは${PASSWORD_MIN_LENGTH}文字以上にしてください。`
    case 'auth/password-does-not-meet-requirements':
      return 'パスワードが要件を満たしていません。英数字を組み合わせるなどしてお試しください。'
    case 'auth/too-many-requests':
      return '試行回数が多すぎます。しばらく時間をおいてお試しください。'
    case 'auth/network-request-failed':
      return '通信に失敗しました。電波状況をご確認のうえ、もう一度お試しください。'
    default:
      return 'エラーが発生しました。しばらく時間をおいてお試しください。'
  }
}

/** 新しいパスワードの入力チェック。問題なければ null。 */
export function passwordValidationMessage(
  password: string,
  confirmation: string
): string | null {
  if (!password || !confirmation) {
    return '新しいパスワードを入力してください。'
  }
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `パスワードは${PASSWORD_MIN_LENGTH}文字以上にしてください。`
  }
  if (password !== confirmation) {
    return '確認用のパスワードが一致しません。'
  }
  return null
}
